import { Temporal } from "@js-temporal/polyfill";

/**
 * Time helpers for the request-scoped timezone model (Model B).
 *
 * Calendar boundaries — "today", "past/future", planner active start — live in
 * the customer's wall-clock day, not in UTC. We don't persist a per-customer
 * timezone; the customer-app sends `X-Client-Timezone` on every request and
 * `requestTimezone` validates it. Helpers here translate between that IANA zone
 * and the date-only / instant values we store.
 *
 * Date-only values (`@db.Date` columns like `PlannedWorkout.date`) round-trip
 * as YMD strings; we hydrate them to `Temporal.PlainDate` for arithmetic and
 * back to UTC-midnight `Date` for Prisma when a query needs a `gte/lte` bound.
 */

/** YMD string (e.g. `"2026-05-23"`) for the calendar day "now" is in inside `zone`. */
export function localTodayYmd(zone: string, now: Date = new Date()): string {
  const instant = Temporal.Instant.fromEpochMilliseconds(now.getTime());
  return instant.toZonedDateTimeISO(zone).toPlainDate().toString();
}

/**
 * First instant of `ymd` in `zone`, returned as a `Date` for Prisma `@db.Date`
 * / `DateTime` comparisons. Crossing the DST spring-forward boundary picks the
 * later wall-clock instant (Temporal's default), which matches "start of day".
 */
export function instantAtLocalMidnight(ymd: string, zone: string): Date {
  const pd = Temporal.PlainDate.from(ymd);
  const zdt = pd.toZonedDateTime({ timeZone: zone });
  return new Date(zdt.epochMilliseconds);
}

/**
 * UTC-midnight (00:00:00.000Z) of the customer's LOCAL calendar today — the
 * correct lower bound for bucketing date-only columns that are stored at
 * UTC-midnight (`PlannedWorkout.date` @db.Date; `CustomerEvent.eventDate`
 * written at `${ymd}T00:00:00Z`). Use THIS, not `instantAtLocalMidnight`, for
 * date-only comparisons: a zoned instant (e.g. LA today → 07:00Z) sits LATER
 * than the stored 00:00Z, which files today's rows as "past" for every
 * negative-UTC offset (the entire Americas). "Today" is still the athlete's
 * local calendar day (via `localTodayYmd`); only the boundary is UTC-midnight.
 */
export function utcMidnightOfLocalToday(zone: string, now: Date = new Date()): Date {
  return new Date(`${localTodayYmd(zone, now)}T00:00:00.000Z`);
}

/** Shift a YMD string by `days` calendar days (TZ-independent). */
export function addDaysYmd(ymd: string, days: number): string {
  return Temporal.PlainDate.from(ymd).add({ days }).toString();
}

/** Lexicographic YMD comparison; safe because `YYYY-MM-DD` sorts correctly. */
export function compareYmd(a: string, b: string): -1 | 0 | 1 {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Whole calendar days from `from` to `to` (positive when `to` is later). */
export function daysBetweenYmd(from: string, to: string): number {
  return Temporal.PlainDate.from(from).until(Temporal.PlainDate.from(to)).days;
}
